import React, { useState } from 'react';
import axios from 'axios';
import { getCSRFToken } from '../utils/csrf';
import './Groups.css';

const GroupImportModal = ({ onClose, onImportComplete, gradeLevel, groupType }) => {
    const [step, setStep] = useState(1); // 1: upload, 2: processing, 3: results
    const [file, setFile] = useState(null);
    const [importResults, setImportResults] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');

    const handleFileUpload = (event) => {
        const uploadedFile = event.target.files[0];
        if (uploadedFile) {
            // Check file type
            const allowedTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ];
            
            if (!allowedTypes.includes(uploadedFile.type) && !uploadedFile.name.endsWith('.csv')) {
                setError('Please upload an Excel file (.xlsx, .xls) or CSV file');
                return;
            }
            
            setFile(uploadedFile);
            setError('');
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            setError('');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleImport = async () => {
        if (!file) {
            setError('Please select a file to import');
            return;
        }

        if (!gradeLevel) {
            setError('Please select a grade level first');
            return;
        }

        setIsProcessing(true);
        setStep(2);
        setError('');

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('grade_level', gradeLevel);
            formData.append('group_type', groupType);

            const response = await axios.post(
                '/api/groups/import-groups/',
                formData,
                {
                    headers: {
                        'X-CSRFToken': getCSRFToken(),
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setImportResults(response.data.results);
            setStep(3);
            onImportComplete(response.data.results);

        } catch (error) {
            console.error('Import error:', error);
            setError(error.response?.data?.error || 'Error importing groups. Please try again.');
            setStep(1);
        } finally {
            setIsProcessing(false);
        }
    };

    const resetModal = () => {
        setStep(1);
        setFile(null);
        setImportResults(null);
        setError('');
    };

    const formatExample = () => {
        if (groupType === 'core') {
            return (
                <table className="example-table">
                    <thead>
                        <tr>
                            <th>Core 1</th>
                            <th>Core 2</th>
                            <th>Core 3</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Smith, John</td>
                            <td>Doe, Jane</td>
                            <td>Johnson, Bob</td>
                        </tr>
                        <tr>
                            <td>Williams, Sarah</td>
                            <td>Brown, Mike</td>
                            <td>Davis, Lisa</td>
                        </tr>
                    </tbody>
                </table>
            );
        } else if (groupType === 'homeroom') {
            return (
                <table className="example-table">
                    <thead>
                        <tr>
                            <th>Homeroom A</th>
                            <th>Homeroom B</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Smith, John</td>
                            <td>Doe, Jane</td>
                        </tr>
                        <tr>
                            <td>Williams, Sarah</td>
                            <td>Brown, Mike</td>
                        </tr>
                    </tbody>
                </table>
            );
        } else {
            return (
                <table className="example-table">
                    <thead>
                        <tr>
                            <th>Welding 101</th>
                            <th>Culinary Arts</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Smith, John</td>
                            <td>Doe, Jane</td>
                        </tr>
                        <tr>
                            <td>Williams, Sarah</td>
                            <td>Brown, Mike</td>
                        </tr>
                    </tbody>
                </table>
            );
        }
    };

    return (
        <div className="modal-overlay">
            <div className="import-modal">
                <div className="modal-header">
                    <h3>Import {groupType === 'core' ? 'Core Class' : groupType === 'homeroom' ? 'Homeroom' : 'CTE'} Groups</h3>
                    <button onClick={onClose} className="close-button">×</button>
                </div>

                <div className="modal-content">
                    {step === 1 && (
                        <div className="upload-step">
                            <h4>Step 1: Upload Your Spreadsheet</h4>
                            
                            <div 
                                className={`drop-zone ${file ? 'has-file' : ''}`}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                            >
                                <div className="drop-content">
                                    <div className="upload-icon">📊</div>
                                    {file ? (
                                        <div>
                                            <p className="file-name">{file.name}</p>
                                            <p className="file-size">({(file.size / 1024).toFixed(1)} KB)</p>
                                            <button 
                                                className="change-file-btn"
                                                onClick={() => setFile(null)}
                                            >
                                                Change File
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <p>Drag your spreadsheet file here</p>
                                            <p>or</p>
                                            <input 
                                                type="file" 
                                                accept=".xlsx,.xls,.csv"
                                                onChange={handleFileUpload}
                                                className="file-input"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="format-examples">
                                <h5>Expected Format:</h5>
                                <p>Each column represents a group. The first row contains group names, and subsequent rows contain student names.</p>
                                <div className="example-container">
                                    {formatExample()}
                                </div>
                                <p className="format-note">
                                    <strong>Note:</strong> Student names can be in "Last, First" or "First Last" format.
                                </p>
                            </div>

                            {error && (
                                <div className="error-message">
                                    {error}
                                </div>
                            )}

                            <div className="step-actions">
                                <button onClick={onClose} className="secondary-btn">Cancel</button>
                                <button 
                                    onClick={handleImport}
                                    className="primary-btn"
                                    disabled={!file || !gradeLevel}
                                >
                                    Import Groups
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="processing-step">
                            <div className="processing-content">
                                <div className="spinner"></div>
                                <h4>Processing Your Spreadsheet...</h4>
                                <p>This may take a moment depending on the number of students.</p>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="results-step">
                            <h4>Import Complete!</h4>
                            
                            <div className="success-summary">
                                <div className="success-item">
                                    <span className="icon">✅</span>
                                    <span>{importResults.groups_created} groups created</span>
                                </div>
                                <div className="success-item">
                                    <span className="icon">✅</span>
                                    <span>{importResults.students_created} new students added</span>
                                </div>
                                <div className="success-item">
                                    <span className="icon">✅</span>
                                    <span>{importResults.students_enrolled} total students enrolled</span>
                                </div>
                            </div>

                            {importResults.created_groups && importResults.created_groups.length > 0 && (
                                <div className="created-groups">
                                    <h5>Created Groups:</h5>
                                    <div className="groups-list">
                                        {importResults.created_groups.map((group, index) => (
                                            <div key={index} className="group-item">
                                                <strong>{group.name}</strong> - {group.student_count} students
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {importResults.errors && importResults.errors.length > 0 && (
                                <div className="import-warnings">
                                    <h5>⚠️ Items to Review:</h5>
                                    {importResults.errors.map((error, index) => (
                                        <div key={index} className="warning-item">{error}</div>
                                    ))}
                                </div>
                            )}

                            <div className="next-steps">
                                <h5>What's Next?</h5>
                                <ul>
                                    <li>✅ Your groups are ready for scheduling</li>
                                    <li>✅ Use the Schedule Tool to assign teachers</li>
                                    <li>✅ Drag groups into time slots to create assignments</li>
                                    <li>✅ Add new students anytime using existing tools</li>
                                </ul>
                            </div>

                            <div className="step-actions">
                                <button onClick={resetModal} className="secondary-btn">Import More Groups</button>
                                <button onClick={onClose} className="primary-btn">Done</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GroupImportModal;
